import { useMemo, useState } from 'react'

import { ПРОБЕГ_В_ВОЗДУХЕ, molecularSpeed, ru, straightTime, walkTime } from '../../lib/physics'
import { Choice, Predict, Slider } from './common'

/**
 * Почему запах идёт минуты (урок 12): молекула духов летит со скоростью сотни метров в секунду,
 * но через каждые доли микрометра налетает на молекулу воздуха и меняет направление. После N ударов
 * она в среднем отходит лишь на λ·√N. В совсем неподвижном воздухе запах шёл бы через комнату
 * больше недели; за минуты его разносят потоки воздуха, а диффузия перемешивает на месте.
 */

type Воздух = 'неподвижный' | 'со сквозняком'

const L = 5 // м — до конца класса
const ПОТОК = 0.1 // м/с — обычное движение воздуха в комнате

/** Случайное блуждание: N шагов одинаковой длины в случайных направлениях. */
function блуждание(N: number, seed: number): [number, number][] {
  let x = seed
  const rand = () => (x = (x * 9301 + 49297) % 233280) / 233280
  const точки: [number, number][] = [[0, 0]]
  let px = 0
  let py = 0
  for (let i = 0; i < N; i++) {
    const угол = 2 * Math.PI * rand()
    px += Math.cos(угол)
    py += Math.sin(угол)
    точки.push([px, py])
  }
  return точки
}

function время(с: number): string {
  if (с < 1) return `${ru(с, 2)} с`
  if (с < 120) return `${ru(с, 0)} с`
  if (с < 7200) return `${ru(с / 60, 0)} мин`
  if (с < 2 * 86400) return `${ru(с / 3600, 0)} ч`
  return `${ru(с / 86400, 0)} сут`
}

export default function SmellLab() {
  const [N, setN] = useState(400)
  const [проба, setПроба] = useState(1)
  const [воздух, setВоздух] = useState<Воздух>('неподвижный')

  const путь = useMemo(() => блуждание(N, 17 + проба * 101), [N, проба])
  const [кx, кy] = путь[путь.length - 1]
  const отошла = Math.hypot(кx, кy)
  const масштаб = 150 / Math.max(Math.sqrt(N) * 1.1, 8) // px на длину пробега

  const v = molecularSpeed(20)
  const прямо = straightTime(L, v)
  const зигзагом = walkTime(L, v)
  const сквозняком = L / ПОТОК

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 300" role="img" aria-label="Путь молекулы запаха среди молекул воздуха">
        <rect x={0} y={0} width={360} height={300} rx={10} className="fill-paper" />
        <polyline
          points={путь.map(([x, y]) => `${180 + x * масштаб},${150 + y * масштаб}`).join(' ')}
          fill="none"
          className="ink-el"
          strokeWidth={1}
        />
        <circle cx={180} cy={150} r={5} className="fill-heavy" />
        <circle cx={180 + кx * масштаб} cy={150 + кy * масштаб} r={5} className="fill-el" />
        <line x1={180} y1={150} x2={180 + кx * масштаб} y2={150 + кy * масштаб} className="ink-heavy" strokeWidth={2} strokeDasharray="5 3" />
        <text x={10} y={290} className="lab-tick">прямой путь — пунктир, весь путь молекулы — ломаная</text>
      </svg>

      <div className="lab-readout">
        <span>пролетела: <b>{N}</b> пробегов</span>
        <span>отошла: <b>{ru(отошла, 0)}</b> пробегов (√{N} ≈ {ru(Math.sqrt(N), 0)})</span>
      </div>

      <Slider label="Число ударов" value={N} min={10} max={2000} step={10} unit="" onChange={setN} />
      <button className="btn" onClick={() => setПроба((p) => p + 1)}>Другая молекула</button>

      <div className="lab-sub" style={{ marginTop: 18 }}>Флакон духов открыт, до последней парты {L} м</div>
      <div className="lab-readout">
        <span>скорость молекулы: {ru(v, 0)} м/с</span>
        <span>пробег между ударами: {ru(ПРОБЕГ_В_ВОЗДУХЕ * 1e9, 0)} нм</span>
      </div>
      <div className="lab-readout">
        <span>по прямой, без ударов: <b>{время(прямо)}</b></span>
        <span>зигзагом, в неподвижном воздухе: <b>{время(зигзагом)}</b></span>
        {воздух === 'со сквозняком' && <span>с потоком воздуха {ru(ПОТОК, 1)} м/с: <b>{время(сквозняком)}</b></span>}
      </div>
      <Choice options={(['неподвижный', 'со сквозняком'] as const).map((x) => ({ value: x, label: `воздух ${x}` }))} value={воздух} onChange={setВоздух} />

      <Predict
        question="Молекула летит со скоростью 500 м/с. Почему запах доходит до конца класса не за сотую долю секунды?"
        options={['молекулы духов тяжёлые', 'молекула всё время сталкивается с молекулами воздуха', 'запах распространяет свет']}
        right={1}
        revealed={N >= 1000}
        explain="Каждый удар меняет направление. После N ударов молекула в среднем отходит лишь на √N пробегов: сто ударов — на десять. Путь огромен, а продвижение маленькое."
      />
      <p className="muted small">
        Если бы воздух в классе совсем не двигался, одной диффузии понадобилась бы неделя. За минуты запах разносят
        потоки воздуха — от дыхания, тёплых батарей, открытой двери, — а диффузия перемешивает его с воздухом на месте.
      </p>
    </div>
  )
}
